import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { API } from "../api";

function campoVisivel(campo, valores) {
  const cond = campo.condicao;
  if (!cond || !cond.campo || !cond.operador) return true;
  const ref = String(valores[cond.campo] || "").trim();
  const val = String(cond.valor || "").trim();
  if (cond.operador === "preenchido") return !!ref;
  if (cond.operador === "vazio") return !ref;
  if (cond.operador === "eq") return ref === val;
  if (cond.operador === "ne") return ref !== val;
  return true;
}

export default function PublicForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const campanha = params.get("c") || "";
  const [schema, setSchema] = useState(null);
  const [erro, setErro] = useState("");
  const [valores, setValores] = useState({});
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [feito, setFeito] = useState("");

  useEffect(() => {
    fetch(`${API}/public/forms/${id}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((sc) => {
        setSchema(sc);
        fetch(`${API}/public/forms/${id}/view`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ campanha }),
        }).catch(() => {});
      })
      .catch(() => setErro("Este formulario nao esta disponivel."));
  }, [id, campanha]);

  const setVal = (k, v) => setValores((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const visiveis = (schema.campos || []).filter((c) => campoVisivel(c, valores));
    const faltam = visiveis.filter((c) => c.obrigatorio && !String(valores[c.key] || "").trim());
    if (faltam.length) { setErro(`Preencha os campos obrigatorios: ${faltam.map((c) => c.label).join(", ")}`); return; }
    setErro(""); setBusy(true);
    try {
      const tok = schema._token || "";
      let r = await fetch(`${API}/public/forms/${id}/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...valores, _hp: hp, _token: tok, _campanha: campanha }),
      });
      if (r.status === 400 && tok) {
        r = await fetch(`${API}/public/forms/${id}/submit`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...valores, _hp: hp, _campanha: campanha }),
        });
      }
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Nao foi possivel enviar.");
      setFeito(data.mensagem || schema.mensagem_sucesso || "Obrigado!");
    } catch (err) {
      setErro(typeof err.message === "string" && err.message ? err.message : "Nao foi possivel enviar. Tente novamente.");
    } finally { setBusy(false); }
  };

  const cor = schema?.cor || "#0B2545";

  if (erro && !schema) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500 p-6" data-testid="public-form-error">{erro}</div>
  );
  if (!schema) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500">
      <Loader2 className="animate-spin mr-2" size={18} /> A carregar...
    </div>
  );

  const visiveis = (schema.campos || []).filter((c) => campoVisivel(c, valores));

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 p-4" style={{ background: `linear-gradient(160deg, ${cor}0d, #f1f5f9 55%)` }}>
      <div className="w-full max-w-lg" data-testid="public-form-page">
        <div className="flex items-center justify-center gap-2 mb-6 text-slate-700">
          <ShieldCheck size={22} style={{ color: cor }} />
          <span className="text-lg font-bold tracking-tight">SaaS-Hub</span>
          <span className="text-sm text-slate-400">by Cogniseg</span>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="px-7 py-5" style={{ background: cor }}>
            <h1 className="text-white text-2xl font-bold tracking-tight" data-testid="public-form-title">{schema.titulo_publico || "Fale connosco"}</h1>
            {schema.descricao && <p className="text-white/75 text-sm mt-1">{schema.descricao}</p>}
          </div>

          {feito ? (
            <div className="p-8 text-center" data-testid="public-form-success">
              <CheckCircle2 className="mx-auto mb-3" size={44} style={{ color: cor }} />
              <div className="text-slate-800 font-semibold text-lg">{feito}</div>
              <p className="text-slate-500 text-sm mt-2">Pode fechar esta pagina.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="p-7 space-y-4" data-testid="public-form">
              <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)}
                style={{ position: "absolute", left: "-9999px", height: 0, width: 0 }} aria-hidden="true" />

              {visiveis.map((c) => (
                <div key={c.key}>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    {c.label}{c.obrigatorio ? " *" : ""}
                  </label>
                  {c.tipo === "textarea" ? (
                    <textarea rows={3} value={valores[c.key] || ""} onChange={(e) => setVal(c.key, e.target.value)}
                      data-testid={`public-field-${c.key}`}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2" style={{ "--tw-ring-color": cor }} />
                  ) : (c.tipo === "select" || c.tipo === "produto") ? (
                    <select value={valores[c.key] || ""} onChange={(e) => setVal(c.key, e.target.value)}
                      data-testid={`public-field-${c.key}`}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2" style={{ "--tw-ring-color": cor }}>
                      <option value="">Selecione...</option>
                      {(c.opcoes || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : c.tipo === "checkbox" ? (
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input type="checkbox" checked={valores[c.key] === "sim"} onChange={(e) => setVal(c.key, e.target.checked ? "sim" : "")} data-testid={`public-field-${c.key}`} /> Sim
                    </label>
                  ) : (
                    <input type={c.tipo === "email" ? "email" : c.tipo === "telefone" ? "tel" : "text"}
                      value={valores[c.key] || ""} onChange={(e) => setVal(c.key, e.target.value)}
                      data-testid={`public-field-${c.key}`}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2" style={{ "--tw-ring-color": cor }} />
                  )}
                </div>
              ))}

              {erro && <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="public-form-erro">{erro}</div>}

              <button type="submit" disabled={busy} data-testid="public-form-submit"
                className="w-full text-white rounded-lg py-3 font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: cor }}>
                {busy && <Loader2 className="animate-spin" size={16} />}{busy ? "A enviar..." : "Enviar"}
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">Protegido contra spam - Cogniseg</p>
      </div>
    </div>
  );
}
